import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Paper, Stack, Typography } from '@mui/material';
import {
  REFERRAL_ARCHIVED,
  REFERRAL_INCOMPLETE,
  referralCopy,
  referralEditChanges,
  referralEditIssues,
  resubmitMovesStatus,
  type ReferralDto,
  type ReferralFormDto,
} from '@hv/shared';
import { ApiError } from '../api/client';
import { useEditReferral, useMyAllotment } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { useNotify } from '../components/Notify';
import { allotmentFor, editStateOf, formFromReferral, pruneBlankChildren, toEditPayload, type DraftForm } from './draft';
import { AllotmentChoice, ApplicantsStep, FamilyStep, ReferralQuestionsStep, type StepProps } from './ReferralSteps';

const STALE_REFERRAL = 'STALE_REFERRAL';

export function ReferralEditForm({
  referral,
  config,
  onDirtyChange,
  onSaved,
  onReload,
}: {
  referral: ReferralDto;
  config: ReferralFormDto;
  onDirtyChange: (dirty: boolean) => void;
  onSaved: () => void;
  onReload: () => void;
}) {
  const { me } = useAuth();
  const { notify, notifyError } = useNotify();
  const usage = useMyAllotment();
  const editReferral = useEditReferral();
  const t = useMemo(() => referralCopy(config.formCopy), [config.formCopy]);
  const [initial] = useState(() => formFromReferral(referral));
  const [form, setForm] = useState<DraftForm>(initial);
  const [issues, setIssues] = useState<string[]>([]);
  const [stale, setStale] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const edit = useCallback((fn: (f: DraftForm) => DraftForm) => setForm(fn), []);
  const dirty = useMemo(
    () => referralEditChanges(editStateOf(initial), editStateOf(form)).kinds.length > 0,
    [initial, form],
  );

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  useEffect(() => {
    if (usage.error) notifyError(usage.error, 'Loading your recommendation usage failed');
  }, [usage.error, notifyError]);

  const scrollTop = () => topRef.current?.scrollIntoView({ block: 'start' });
  const canResubmit = resubmitMovesStatus(referral.status);
  const saving = editReferral.isPending;

  const save = async (resubmit: boolean) => {
    const current = pruneBlankChildren(form);
    const found = referralEditIssues(current, {
      grades: [...config.applyingGrades, ...referral.payload.children.map((c) => c.grade)],
    });
    setIssues(found);
    if (found.length) {
      scrollTop();
      return;
    }
    try {
      const res = await editReferral.mutateAsync({
        id: referral.id,
        version: referral.version,
        payload: toEditPayload(current),
        countsTowardAllotment: current.countsTowardAllotment,
        resubmit,
      });
      const ref = res.referral.referenceNumber ? ` (${res.referral.referenceNumber})` : '';
      notify(
        'success',
        res.resubmitted
          ? `Referral resubmitted to Admissions${ref}`
          : res.changes.length
            ? `Your changes were saved${ref}`
            : 'There were no changes to save',
      );
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.code === STALE_REFERRAL) {
        console.error('Referral edit rejected as stale', { referralId: referral.id, err });
        setStale(true);
        scrollTop();
      } else if (err instanceof ApiError && (err.code === REFERRAL_INCOMPLETE || err.issues.length)) {
        console.error('Referral edit refused', { referralId: referral.id, issues: err.issues });
        setIssues(err.issues.map((i) => i.message));
        scrollTop();
      } else {
        notifyError(err, resubmit ? 'We could not resubmit this referral' : 'We could not save your changes');
        if (err instanceof ApiError && err.code === REFERRAL_ARCHIVED) onReload();
      }
    }
  };

  const props: StepProps = {
    form,
    edit,
    t,
    grades: config.applyingGrades,
    cycles: referral.cycle ? [referral.cycle] : [],
    me,
    allotmentLimit: config.allotmentLimit,
    allotment: usage.data ? allotmentFor(usage.data, form.cycleId, referral.id) : null,
    order: config.formOrder,
    hidden: config.hiddenQuestions,
    locked: true,
    childStages: referral.childStages,
  };

  return (
    <Box ref={topRef} data-testid="referral-edit-form">
      {stale && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={onReload}>
              Reload latest version
            </Button>
          }
        >
          This referral was changed in another tab or window. Reload it to see the latest version; changes made here
          were not saved.
        </Alert>
      )}
      {issues.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} role="alert" data-testid="review-issues">
          <AlertTitle>Please review the following</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </Box>
        </Alert>
      )}
      <Stack gap={2}>
        <Paper sx={{ p: { xs: 2, sm: 3 } }} component="section" aria-label={t('step2_title')}>
          <Typography variant="h3" component="h3" mb={1}>
            {t('step2_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The family name, academic year and parents or guardians can’t be changed after submitting.
          </Typography>
          <FamilyStep {...props} />
        </Paper>
        <Paper sx={{ p: { xs: 2, sm: 3 } }} component="section" aria-label={t('step3_title')}>
          <Typography variant="h3" component="h3" mb={2}>
            {t('step3_title')}
          </Typography>
          <ApplicantsStep {...props} />
        </Paper>
        <Paper sx={{ p: { xs: 2, sm: 3 } }} component="section" aria-label={t('step4_title')}>
          <Typography variant="h3" component="h3" mb={2}>
            {t('step4_title')}
          </Typography>
          <ReferralQuestionsStep {...props} />
        </Paper>
        <AllotmentChoice {...props} />
        <Stack direction="row" justifyContent="flex-end" gap={1} flexWrap="wrap">
          <Button
            variant={canResubmit ? 'outlined' : 'contained'}
            disabled={saving || stale || !dirty}
            onClick={() => void save(false)}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          {canResubmit && (
            <Button variant="contained" disabled={saving || stale} onClick={() => void save(true)}>
              {saving ? 'Sending…' : 'Save and resubmit to Admissions'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
